import { CTA, styled } from "@volkswagen-onehub/components-core";
import { Heart, HeartFilled } from "@volkswagen-onehub/icons-core";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../app-context";
import { useFavouritesStore } from "../store/favourites";
import { SoldIcon } from "../svg/SoldIcon";
import { getLang, getTranslations } from "../translations";
import { DealerData, Language, VehicleData } from "../types";
import { useFeatureServices } from "../use-feature-services";
import { VehicleDetailsContext } from "../utils/details-context";
import { getStep } from "../utils/get-step";
import { createConfiguration, createConstantData, createPartner, createProcess } from "../utils/get-tracking-event-data";
import { useDealersStore } from "../utils/use-dealers-store";
import { Route } from "../utils/use-route";
import { VehicleContext } from "../utils/vehicle-context";

type Props = {
  vin: string;
  ariaModel?: string;
  sold?: boolean;
  ulid?: string;
};

const LikeButtonContainer = styled.div`
  position: relative;
`;

const HoveredMenu = styled.div`
  position: absolute;
  bottom: -15px;
  left: -40%;
  display: flex;
  flex-direction: column;
  row-gap: ${(props) => props.theme.size.dynamic0020};
  transform: translate(-65%, 50%);
  width: 150px;
  background-color: rgb(255, 255, 255);
  padding: 5px 10px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  z-index: 9;
`;

const ContextCTA = styled(CTA)`
  span {
    text-decoration: none;
    font-size: 14px;
  }
`;

const AriaLive = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
`;

const UlidIconContainer = styled.div`
  margin: ${props => props.theme.size.static100};
`;

const ContextMenuList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

export const LikeButton = ({vin, ariaModel, sold, ulid}: Props) => {
  const { globalApi } = useContext(AppContext);
  const route = useLocation();
  const {favourites, updateFavourites} = useFavouritesStore();
  const {tracking} = useFeatureServices();
  const lang = getLang();
  const {labels, aria} = getTranslations();
  const enLabels = getTranslations(Language.EN).labels;
  const navigate = useNavigate();

  const vehicle = useContext(VehicleContext) as VehicleData || useContext(VehicleDetailsContext).vehicle as VehicleData;

  const { isLoading, allDealers: all_dealers } = useDealersStore();

  const dealer = (all_dealers as DealerData[])?.filter(dealer => dealer.static_dealer_id === vehicle?.dealer_id)[0];

  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showAria, setShowAria] = useState(false);

  const step = getStep(route.pathname);

  const getMainAriaLabel = () => {
   if (favourites?.vins?.includes(vin) && (route.pathname.includes("favourites") || route.pathname.includes("details"))) {
     return labels.contextMenu.deselect;
   } else if (favourites?.vins?.includes(vin) && !showMenu) {
      return labels.contextMenu.open;
   } else if (showMenu) {
      return "";
   } else {
      return labels.like_vehicle;
   }
  };

  const getAriaAnnouncement = (vins: string[], showAria: boolean) => {
    if (showAria && !vins?.includes(vin)) {
      return aria.favourites.deselected.replace('%%ariaModel%%', ariaModel).replace('%%favouriteCount%%', vins?.length);
    } else if (showAria && vins?.includes(vin)) {
      return aria.favourites.selected.replace('%%ariaModel%%', ariaModel).replace('%%favouriteCount%%', vins?.length);
    }
    return "";
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (!favourites?.vins?.includes(vin)) {
      tracking.track('VWBasic_VehicleButton_Click', {
        ...createConstantData('VWBasic_VehicleButton_Click', vehicle.category),
        configuration: createConfiguration(
          'VWBasic_VehicleButton_Click',
          vehicle,
          lang,
        ),
        partner: createPartner(dealer),
        process: createProcess(step),
        link: {
          url: window.location.href,
          name: enLabels.like_vehicle,
          isDealerPage: false,
        },
      });
      setShowMenu(false);
      updateFavourites(globalApi, vin);
      setShowAria(true);
    } else {
      if (route.pathname == '/' + Route.RESULTS) {
        setShowMenu((prev) => !prev);
      } else {
        updateFavourites(globalApi, vin);
        setShowAria(true);
      }
    }
  };

  const onDeselect = () => {
    updateFavourites(globalApi, vin);
    setShowMenu(false);
    setShowAria(true);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showAria) {
      timer = setTimeout(() => setShowAria(false), 5000);
    }

    return () => {
      if (!!timer) {
        clearTimeout(timer)
      }
    };
  }, [showAria]);

  const getFavIcon = () => {
    if (ulid && sold) {
      return <SoldIcon ariaHidden />;
    } else if (ulid && !sold) {
      return <HeartFilled ariaHidden />;
    } else if (favourites?.vins?.includes(vin) && sold) {
      return <SoldIcon ariaHidden />;
    } else if (favourites?.vins?.includes(vin) && !sold) {
      return <HeartFilled ariaHidden />;
    }
    return <Heart ariaHidden />;
  }

  return (
    isLoading
    ? <></>
    : <LikeButtonContainer>
      {
      !ulid ? (
        <CTA
          tag="button"
          emphasis="tertiary"
          icon={getFavIcon()}
          ariaLabel={getMainAriaLabel()}
          onClick={(e) => handleClick(e)}
          disabled={ulid != undefined}
        />
        ) : <UlidIconContainer>{getFavIcon()}</UlidIconContainer>
      }

      <div aria-live="polite">
        {showMenu && (
          <HoveredMenu onMouseLeave={() => setShowMenu(false)}>
            <ContextMenuList>
              <li>
                <ContextCTA
                  // ariaLabel={labels.contextMenu.view}
                  tag="a"
                  href="/"
                  emphasis="tertiary"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate({ pathname: '/favourites'})
                  }}
                >
                  {labels.contextMenu.view}
                </ContextCTA>
              </li>
              <li>
                <ContextCTA
                  // ariaLabel={labels.contextMenu.deselect}
                  tag="a"
                  href="/"
                  emphasis="tertiary"
                  onClick={(e) => {
                    e.preventDefault();
                    onDeselect()}
                  }
                >
                  {labels.contextMenu.deselect}
                </ContextCTA>
              </li>
            </ContextMenuList>
          </HoveredMenu>
        )}
      </div>
      <AriaLive aria-live="polite" role="alert">
        { getAriaAnnouncement(favourites.vins as string[], showAria) }
      </AriaLive>
    </LikeButtonContainer>
  );
};
